import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Gauge, TrendingUp, TrendingDown, Save, RotateCcw, CalendarClock, Landmark, Wallet, ReceiptText,
  Hammer, Activity,
} from 'lucide-react';
import api from '../../lib/api';
import { computeCostControl } from '../../lib/engine';
import { fmtMoneyLabel } from '../../lib/format';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { fmtDate } from '../../lib/format';
import { cn } from '../../lib/utils';
import { MobileActionBar } from '../MobileActionBar';

const filsToMajor = (f) => (f == null || f === '' ? '' : f / 100);

function StatCard({ icon: Icon, label, value, sub, tone = 'foreground', testid }) {
  return (
    <div className="erak-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Icon size={14} />{label}</div>
      <div className={cn('mt-1 font-num text-xl font-bold', `text-[hsl(var(--${tone}))]`)} data-testid={`${testid}-value`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export function CostControl({ projectId, currency = 'AED' }) {
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState('');
  const [actualsInput, setActualsInput] = useState({});
  const [saving, setSaving] = useState(false);

  const hydrate = (d) => {
    setData(d);
    setAsOf(d.actualsAsOf || '');
    const inp = {};
    (d.dashboard.categories || []).forEach((c) => { inp[c.category] = filsToMajor(c.actual); });
    setActualsInput(inp);
  };

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/projects/${projectId}/cost-control`);
      hydrate(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the cost-control dashboard');
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const money = (f) => fmtMoneyLabel(f, currency);

  // Live recompute of the dashboard from edited actuals (fils-exact, mirrors backend).
  const live = useMemo(() => {
    if (!data) return null;
    const cats = (data.dashboard.categories || []).map((c) => ({
      category: c.category, budget: c.budget, committed: c.committed,
      actual: Math.round((Number(actualsInput[c.category]) || 0) * 100),
    }));
    return computeCostControl(data.baseline.contractValue, cats, data.invoiceLines,
      data.earnedBottomUp, data.hasCertifiedExecution);
  }, [data, actualsInput]);

  if (!data || !live) return <div className="h-72 animate-pulse rounded-2xl bg-muted/40" data-testid="cost-control-loading" />;

  const canPost = data.permissions?.canPostActuals;
  const m = live.margins;
  const t = live.totals;
  const inv = data.dashboard.cumulativeInvoiced;

  const saveActuals = async () => {
    setSaving(true);
    try {
      const byCategory = {};
      Object.entries(actualsInput).forEach(([k, v]) => { if (v !== '' && v != null) byCategory[k] = Number(v) || 0; });
      const { data: r } = await api.put(`/projects/${projectId}/actuals`, { asOf: asOf || null, byCategory });
      toast.success('SAP actuals posted');
      await load();
      return r;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post actuals');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="cost-control">
      {/* Baseline strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Landmark} label="Contract Value" value={money(data.baseline.contractValue)}
          sub={data.baseline.ccNo ? `CC ${data.baseline.ccNo}` : null} tone="brand" testid="cc-contract-value" />
        <StatCard icon={Wallet} label="Budget (frozen baseline)" value={money(data.baseline.budget)} testid="cc-budget-total" />
        <StatCard icon={ReceiptText} label="Cumulative Invoiced" value={money(inv.total)}
          sub={`Certified ${money(inv.certified)} · Paid ${money(inv.paid)}`} testid="cc-cumulative-invoiced" />
        <StatCard icon={CalendarClock} label="Actuals as of"
          value={data.actualsAsOf ? fmtDate(data.actualsAsOf) : '—'}
          sub={data.actualsAsOf ? 'SAP posted' : 'No actuals posted yet'} testid="cc-actuals-asof" />
      </div>

      {/* Margin panel */}
      <div className="grid gap-3 md:grid-cols-3">
        <div className="erak-card p-4" data-testid="cc-budgeted-margin">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp size={14} />Budgeted Margin</div>
          <div className="mt-1 font-num text-2xl font-bold text-[hsl(var(--success))]">{money(m.budgetedMargin)}</div>
          <div className="text-xs text-muted-foreground">{m.budgetedMarginPct}% of contract · Value − Budget</div>
        </div>
        <div className="erak-card p-4" data-testid="cc-projected-margin">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><Gauge size={14} />Projected Margin</div>
          <div className={cn('mt-1 font-num text-2xl font-bold', m.projectedMargin >= m.budgetedMargin ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]')}>{money(m.projectedMargin)}</div>
          <div className="text-xs text-muted-foreground">{m.projectedMarginPct}% of contract · Value − Actual</div>
        </div>
        <div className={cn('erak-card p-4', m.erosionUnfavourable && 'border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.06)]')} data-testid="cc-margin-erosion">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {m.erosionUnfavourable ? <TrendingDown size={14} className="text-[hsl(var(--danger))]" /> : <TrendingUp size={14} className="text-[hsl(var(--success))]" />}
            Margin Erosion
          </div>
          <div className={cn('mt-1 font-num text-2xl font-bold', m.erosionUnfavourable ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--success))]')} data-testid="cc-erosion-value">
            {m.marginErosion > 0 ? '−' : m.marginErosion < 0 ? '+' : ''}{money(Math.abs(m.marginErosion))}
          </div>
          <div className="text-xs text-muted-foreground">{m.erosionUnfavourable ? 'Actuals exceed budget — margin eroding' : 'Actuals within budget'}</div>
        </div>
      </div>

      {/* WIP panel — Earned (bottom-up & POC) vs Cumulative Invoiced */}
      {(() => {
        const w = live.wip;
        const wipTone = (v) => (v > 0 ? 'warning' : v < 0 ? 'danger' : 'success');
        const wipLabel = (v) => (v > 0 ? 'Under-billed · to invoice' : v < 0 ? 'Over-billed' : 'Fully billed');
        const signed = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${money(Math.abs(v))}`;
        return (
          <div className="erak-card p-4" data-testid="cc-wip-panel">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 font-display text-base font-semibold"><Hammer size={16} className="text-[hsl(var(--brand))]" />Work in Progress (WIP)</h3>
              {!w.hasCertifiedExecution && (
                <Badge variant="outline" className="gap-1 border-[hsl(var(--warning)/0.4)] text-[11px] text-[hsl(var(--warning))]" data-testid="cc-wip-no-exec">
                  <TrendingDown size={11} />No certified execution yet — bottom-up earned is 0
                </Badge>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-xl border p-3" data-testid="cc-wip-earned-bottomup">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Activity size={13} />Earned (bottom-up)</div>
                <div className="mt-1 font-num text-lg font-bold">{money(w.earnedBottomUp)}</div>
                <div className="text-[10px] text-muted-foreground">Certified qty × Sale Rate</div>
              </div>
              <div className="rounded-xl border p-3" data-testid="cc-wip-earned-poc">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Gauge size={13} />Earned (POC)</div>
                <div className="mt-1 font-num text-lg font-bold">{money(w.earnedPoc)}</div>
                <div className="text-[10px] text-muted-foreground">(Actual ÷ Budget) × Value</div>
              </div>
              <div className="rounded-xl border p-3" data-testid="cc-wip-invoiced">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><ReceiptText size={13} />Cumulative Invoiced</div>
                <div className="mt-1 font-num text-lg font-bold">{money(w.cumulativeInvoiced)}</div>
                <div className="text-[10px] text-muted-foreground">Certified + Paid</div>
              </div>
              <div className={cn('rounded-xl border p-3', `border-[hsl(var(--${wipTone(w.wipBottomUp)})/0.4)] bg-[hsl(var(--${wipTone(w.wipBottomUp)})/0.06)]`)} data-testid="cc-wip-bottomup">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {w.wipBottomUp >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}WIP (bottom-up)
                </div>
                <div className={cn('mt-1 font-num text-xl font-bold', `text-[hsl(var(--${wipTone(w.wipBottomUp)}))]`)} data-testid="cc-wip-bottomup-value">{signed(w.wipBottomUp)}</div>
                <div className={cn('text-[10px]', `text-[hsl(var(--${wipTone(w.wipBottomUp)}))]`)}>{wipLabel(w.wipBottomUp)}</div>
              </div>
              <div className="rounded-xl border p-3" data-testid="cc-wip-poc">
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {w.wipPoc >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}WIP (POC)
                </div>
                <div className={cn('mt-1 font-num text-lg font-bold', `text-[hsl(var(--${wipTone(w.wipPoc)}))]`)} data-testid="cc-wip-poc-value">{signed(w.wipPoc)}</div>
                <div className="text-[10px] text-muted-foreground">{wipLabel(w.wipPoc)}</div>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">Headline WIP is the bottom-up figure. Positive = under-billed (still to invoice); negative = over-billed.</p>
          </div>
        );
      })()}

      {/* 3-way category table */}
      <div className="erak-card overflow-hidden p-0" data-testid="cc-category-table">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-display text-base font-semibold">Budget vs Committed vs Actual</h3>
          {canPost && (
            <div className="hidden items-center gap-2 md:flex">
              <Button variant="outline" size="sm" onClick={load} data-testid="cc-reset"><RotateCcw size={14} className="mr-1.5" />Reset</Button>
              <Button size="sm" onClick={saveActuals} disabled={saving} data-testid="cc-save-actuals"><Save size={14} className="mr-1.5" />{saving ? 'Posting…' : 'Post actuals'}</Button>
            </div>
          )}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Category</th>
                <th className="px-3 py-2 text-right font-medium">Budget</th>
                <th className="px-3 py-2 text-right font-medium">Committed</th>
                <th className="px-3 py-2 text-right font-medium">Actual (SAP)</th>
                <th className="px-3 py-2 text-right font-medium">Bal. to Commit</th>
                <th className="px-3 py-2 text-right font-medium">Bal. vs Actual</th>
                <th className="px-3 py-2 text-right font-medium">Committed − Actual</th>
              </tr>
            </thead>
            <tbody>
              {live.categories.map((c, i) => (
                <tr key={c.category} data-testid={`cc-row-${c.category}`} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2 font-medium">{c.category}</td>
                  <td className="px-3 py-2 text-right font-num">{money(c.budget)}</td>
                  <td className="px-3 py-2 text-right font-num">{money(c.committed)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {canPost ? (
                      <Input type="number" step="0.01" className="h-8 w-32 text-right font-num" data-testid={`cc-actual-input-${c.category}`}
                        value={actualsInput[c.category] ?? ''} onChange={(e) => setActualsInput((a) => ({ ...a, [c.category]: e.target.value }))} />
                    ) : (
                      <span className="font-num" data-testid={`cc-actual-${c.category}`}>{money(c.actual)}</span>
                    )}
                  </td>
                  <td className={cn('px-3 py-2 text-right font-num', c.balanceToCommit < 0 && 'text-[hsl(var(--danger))]')}>{money(c.balanceToCommit)}</td>
                  <td className={cn('px-3 py-2 text-right font-num', c.balanceVsActual < 0 && 'text-[hsl(var(--danger))]')}>{money(c.balanceVsActual)}</td>
                  <td className={cn('px-3 py-2 text-right font-num', c.committedVsActual < 0 && 'text-[hsl(var(--danger))]')}>{money(c.committedVsActual)}</td>
                </tr>
              ))}
              {live.categories.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No budget categories on this project yet.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 bg-muted/30 font-num font-semibold">
                <td className="px-3 py-2">Totals</td>
                <td className="px-3 py-2 text-right" data-testid="cc-total-budget">{money(t.budget)}</td>
                <td className="px-3 py-2 text-right" data-testid="cc-total-committed">{money(t.committed)}</td>
                <td className="px-3 py-2 text-right" data-testid="cc-total-actual">{money(t.actual)}</td>
                <td className={cn('px-3 py-2 text-right', t.balanceToCommit < 0 && 'text-[hsl(var(--danger))]')} data-testid="cc-total-balance-commit">{money(t.balanceToCommit)}</td>
                <td className={cn('px-3 py-2 text-right', t.balanceVsActual < 0 && 'text-[hsl(var(--danger))]')} data-testid="cc-total-balance-actual">{money(t.balanceVsActual)}</td>
                <td className={cn('px-3 py-2 text-right', t.committedVsActual < 0 && 'text-[hsl(var(--danger))]')} data-testid="cc-total-committed-actual">{money(t.committedVsActual)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile: stacked cards (no side-scrolling) */}
        <div className="divide-y md:hidden" data-testid="cc-category-cards">
          {live.categories.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No budget categories on this project yet.</div>
          )}
          {live.categories.map((c) => (
            <div key={c.category} className="p-4" data-testid={`cc-card-${c.category}`}>
              <div className="mb-2 font-display text-sm font-semibold">{c.category}</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Budget</div><div className="font-num font-medium">{money(c.budget)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Committed</div><div className="font-num font-medium">{money(c.committed)}</div></div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Actual (SAP)</div>
                  {canPost ? (
                    <Input type="number" step="0.01" inputMode="decimal" className="mt-1 h-10 w-full text-right font-num" data-testid={`cc-actual-input-m-${c.category}`}
                      value={actualsInput[c.category] ?? ''} onChange={(e) => setActualsInput((a) => ({ ...a, [c.category]: e.target.value }))} />
                  ) : (
                    <div className="font-num font-medium">{money(c.actual)}</div>
                  )}
                </div>
                <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Bal. to Commit</div><div className={cn('font-num', c.balanceToCommit < 0 && 'text-[hsl(var(--danger))]')}>{money(c.balanceToCommit)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Bal. vs Actual</div><div className={cn('font-num', c.balanceVsActual < 0 && 'text-[hsl(var(--danger))]')}>{money(c.balanceVsActual)}</div></div>
                <div className="col-span-2"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Committed − Actual</div><div className={cn('font-num', c.committedVsActual < 0 && 'text-[hsl(var(--danger))]')}>{money(c.committedVsActual)}</div></div>
              </div>
            </div>
          ))}
          {live.categories.length > 0 && (
            <div className="flex items-center justify-between bg-muted/30 px-4 py-3 font-num font-semibold" data-testid="cc-card-totals">
              <span>Totals</span>
              <span className="text-right text-xs font-normal text-muted-foreground">B {money(t.budget)} · C {money(t.committed)} · A {money(t.actual)}</span>
            </div>
          )}
        </div>
        {canPost && (
          <div className="flex flex-wrap items-center gap-3 border-t px-4 py-3">
            <Label className="text-xs">Actuals as of</Label>
            <Input type="date" className="h-8 w-44" value={asOf || ''} onChange={(e) => setAsOf(e.target.value)} data-testid="cc-asof-input" />
            <Badge variant="outline" className="text-[11px]">Enter SAP-posted actuals per category, then Post actuals</Badge>
          </div>
        )}
      </div>

      {canPost && (
        <MobileActionBar>
          <Button variant="outline" onClick={load} data-testid="cc-reset-m" className="flex-1"><RotateCcw size={15} className="mr-1.5" />Reset</Button>
          <Button onClick={saveActuals} disabled={saving} data-testid="cc-save-actuals-m" className="flex-[2]"><Save size={15} className="mr-1.5" />{saving ? 'Posting…' : 'Post actuals'}</Button>
        </MobileActionBar>
      )}
    </div>
  );
}
